// ============================================================
// creacionesPage.js — Página "Mis Creaciones"
// ------------------------------------------------------------
// Carga las creaciones del usuario desde la API y las muestra
// en la cuadrícula de la galería.
// ============================================================
import { qs, qsa, on } from './utils.js';
import { listHistory } from './api.js';
import { toast } from './toast.js';

export async function initCreacionesPage() {
  if (document.body.dataset.page !== 'creaciones') return;

  const grid = qs('.gallery-grid');
  if (!grid) return;

  // Limpiar placeholders estáticos
  grid.innerHTML = `
    <div class="text-center py-5 w-100" id="creationsLoading">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <p class="mt-2 text-muted">Cargando tus creaciones...</p>
    </div>
  `;

  try {
    const items = await listHistory();

    grid.innerHTML = ''; // Limpiar spinner

    if (items.length === 0) {
      grid.innerHTML = `
        <div class="text-center py-5 w-100">
          <i class="bi bi-folder-x" style="font-size: 3rem; color: var(--text-muted);"></i>
          <h3 class="mt-3">No tienes creaciones</h3>
          <p class="text-muted">Las imágenes, vídeos o avatares que generes aparecerán aquí.</p>
        </div>
      `;
      return;
    }

    // Elementos del modal de vista previa
    const modal = qs('#previewModal');
    const mediaContainer = qs('#previewMediaContainer');
    const promptEl = qs('#previewPrompt');
    const closeBtn = qs('#closePreviewBtn');

    function openPreviewModal(item) {
      if (!modal || !mediaContainer || !promptEl) return;
      
      mediaContainer.innerHTML = '';
      
      const isVideo = item.type === 'video' || item.type === 'avatar' || item.type === 'template';
      const isImageUrl = item.url && /\.(png|jpe?g|gif|webp|avif)$/i.test(item.url);

      if (isVideo && !isImageUrl) {
        const video = document.createElement('video');
        video.src = item.url;
        video.controls = true;
        video.autoplay = true;
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.objectFit = 'contain';
        mediaContainer.appendChild(video);
      } else {
        const img = document.createElement('img');
        img.src = item.url;
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'contain';
        mediaContainer.appendChild(img);
      }
      
      promptEl.textContent = item.title || item.prompt || '';
      
      modal.style.display = 'flex';
      setTimeout(() => {
        modal.style.opacity = '1';
        modal.firstElementChild.style.transform = 'scale(1)';
      }, 10);
    }

    function closePreviewModal() {
      if (!modal) return;
      modal.style.opacity = '0';
      modal.firstElementChild.style.transform = 'scale(0.9)';
      
      const video = mediaContainer?.querySelector('video');
      if (video) {
        video.pause();
      }
      
      setTimeout(() => {
        modal.style.display = 'none';
        if (mediaContainer) mediaContainer.innerHTML = '';
      }, 300);
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', closePreviewModal);
    }
    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target === modal) closePreviewModal();
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal && modal.style.display === 'flex') {
        closePreviewModal();
      }
    });

    items.forEach(item => {
      let metaObj = {};
      try {
        if (typeof item.meta === 'string') metaObj = JSON.parse(item.meta);
        else if (item.meta) metaObj = item.meta;
      } catch {}

      const displayPhoto = metaObj.photoUrl || metaObj.photo || item.url || item.thumb || '/uploads/avatar_annie.png';
      const isPending = item.status === 'queued' || item.status === 'processing';
      const isVideo = (item.type === 'video' || item.type === 'avatar' || item.type === 'template') && !isPending;
      const isImageUrl = item.url && /\.(png|jpe?g|gif|webp|avif)$/i.test(item.url);

      const card = document.createElement('div');
      card.className = 'gen-card has-result';
      card.style.cursor = 'pointer';
      card.style.position = 'relative';

      let mediaHtml = '';
      if (isPending) {
        mediaHtml = `
          <img src="${displayPhoto}" alt="${item.title}" style="width:100%; height:100%; object-fit:cover; border-radius:12px; filter: brightness(0.75);" />
          <div style="position:absolute; top:12px; left:12px; right:12px; background:rgba(99,102,241,0.9); color:white; font-size:0.75rem; font-weight:600; padding:6px 10px; border-radius:20px; display:flex; align-items:center; gap:6px; backdrop-filter:blur(4px); box-shadow:0 4px 12px rgba(0,0,0,0.4);">
            <div class="spinner-border spinner-border-sm" role="status"></div>
            <span>Generando vídeo IA...</span>
          </div>
        `;
      } else if (isVideo && !isImageUrl) {
        mediaHtml = `
          <video src="${item.url}" poster="${displayPhoto}" muted loop playsinline style="width:100%; height:100%; object-fit:cover; border-radius:12px;"></video>
          <span class="history-thumb-play" style="position:absolute; top:50%; left:50%; transform:translate(-50%, -50%); background:rgba(0,0,0,0.6); color:white; border-radius:50%; width:40px; height:40px; display:flex; align-items:center; justify-content:center; pointer-events:none;">
            <i class="bi bi-play-fill" style="font-size: 1.5rem; margin-left: 2px;"></i>
          </span>
        `;
      } else {
        mediaHtml = `<img src="${displayPhoto}" alt="${item.title}" style="width:100%; height:100%; object-fit:cover; border-radius:12px;" />`;
      }

      card.innerHTML = `
        ${mediaHtml}
        <div class="gen-card-actions">
          <button class="gen-card-action" type="button" aria-label="Descargar" data-download-url="${item.url || displayPhoto}">
            <i class="bi bi-download"></i>
          </button>
          <button class="gen-card-action btn-del-gen" type="button" aria-label="Eliminar" data-id="${item.id}" style="color: #ff4d4f;">
            <i class="bi bi-trash"></i>
          </button>
        </div>
      `;

      // Evento eliminar
      const delBtn = card.querySelector('.btn-del-gen');
      if (delBtn) {
        delBtn.addEventListener('click', async (e) => {
          e.stopPropagation();
          if (confirm('¿Deseas eliminar esta creación?')) {
            try {
              const { deleteGeneration } = await import('./api.js');
              await deleteGeneration(item.id);
              card.remove();
            } catch (err) {
              console.error('Error al eliminar:', err);
              card.remove();
            }
          }
        });
      }

      card.addEventListener('click', (e) => {
        if (e.target.closest('.gen-card-action')) return;
        openPreviewModal({
          ...item,
          url: item.url || displayPhoto,
        });
      });

      if (isVideo && !isImageUrl) {
        const video = card.querySelector('video');
        if (video) {
          card.addEventListener('mouseenter', () => video.play().catch(() => {}));
          card.addEventListener('mouseleave', () => {
            video.pause();
            video.currentTime = 0;
          });
        }
      }

      grid.appendChild(card);
    });

    // Polling si hay elementos pendientes
    const pendingItems = items.filter(item => (item.status === 'queued' || item.status === 'processing') && item.jobId);
    if (pendingItems.length > 0) {
      const { getJobStatus } = await import('./api.js');
      pendingItems.forEach(item => {
        let attempts = 0;
        const interval = setInterval(async () => {
          attempts++;
          try {
            const statusRes = await getJobStatus(item.jobId);
            if (statusRes.status === 'completed' || statusRes.status === 'failed' || attempts > 5) {
              clearInterval(interval);
              initCreacionesPage();
            }
          } catch {
            if (attempts > 3) {
              clearInterval(interval);
              initCreacionesPage();
            }
          }
        }, 3000);
      });
    }

  } catch (err) {
    console.error('[Creaciones] Error al cargar creaciones:', err);
    grid.innerHTML = `
      <div class="alert alert-danger w-100" role="alert">
        Error al cargar tus creaciones. Asegúrate de haber iniciado sesión.
      </div>
    `;
  }
}
